/**
 * ConfirmSaleConfirmationModal - Tipo 1: Modal de Confirmación
 *
 * Mismo diseño que CloseConfirmationModal ("Perderás los cambios…").
 * When the order has line(s) or total at Bs 0, requires an explicit checkbox ack.
 * The confirm button stays the default (green) variant; the checkbox carries the warning.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { ConfirmationModal } from '../../ui/Modal';
import { SALES_CONFIRMATION_DIALOG_Z_INDEX } from '../../sales/shared/salesModalStack';

export type ZeroSaleAckKind = 'full' | 'partial';

export interface ConfirmSaleConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  /** True when any line finalPrice is 0 or order grand total is 0. */
  requiresZeroSaleAck?: boolean;
  /** Entire sale at Bs 0 vs some courtesy lines with a remaining charge. */
  zeroSaleAckKind?: ZeroSaleAckKind;
  title?: string;
  message?: string;
  confirmText?: string;
  dialogZIndex?: number;
}

const DEFAULT_HEADING = '¿Confirmar esta venta?';
const DEFAULT_BODY =
  'Se registrará la venta y se actualizará el inventario según las piezas marcadas como entregadas.';

const FULL_ZERO_HEADING = '¿Confirmar venta de cortesía?';
const FULL_ZERO_BODY =
  'Esta venta queda en Bs 0. No se registra cobro. El inventario se actualiza según las piezas marcadas como entregadas.';
const FULL_ZERO_CHECKBOX = 'Confirmo que es una venta de cortesía (regalo), sin cobro.';

const PARTIAL_ZERO_BODY = 'Hay piezas en Bs 0 (cortesía). El resto se cobra con normalidad.';
const PARTIAL_ZERO_CHECKBOX = 'Confirmo las piezas de cortesía en Bs 0.';

const ConfirmSaleConfirmationModal: React.FC<ConfirmSaleConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  requiresZeroSaleAck = false,
  zeroSaleAckKind = 'full',
  title,
  message,
  confirmText = 'Confirmar',
  dialogZIndex = SALES_CONFIRMATION_DIALOG_Z_INDEX,
}) => {
  const isFullZero = requiresZeroSaleAck && zeroSaleAckKind === 'full';
  const isPartialZero = requiresZeroSaleAck && zeroSaleAckKind === 'partial';

  const heading = title ?? (isFullZero ? FULL_ZERO_HEADING : DEFAULT_HEADING);
  const body =
    message ?? (isFullZero ? FULL_ZERO_BODY : isPartialZero ? PARTIAL_ZERO_BODY : DEFAULT_BODY);
  const checkboxLabel = isFullZero
    ? FULL_ZERO_CHECKBOX
    : isPartialZero
      ? PARTIAL_ZERO_CHECKBOX
      : undefined;

  return (
    <ConfirmationModal
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={heading}
      message={heading}
      confirmText={confirmText}
      cancelText="Volver"
      variant="default"
      loading={loading}
      icon={<CheckCircleIcon />}
      requireCheckbox={requiresZeroSaleAck}
      checkboxLabel={checkboxLabel}
      dialogZIndex={dialogZIndex}
      customContent={
        <Box>
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        </Box>
      }
    />
  );
};

export default ConfirmSaleConfirmationModal;
